// @ts-strict-ignore
import { UserModel } from '@user/model/user.model';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProjectHeadVO, ProjectSaveInfoVO, ProjectVO } from '@project/model/vo/project.vo';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { ProjectModel } from '@project/model/project.model';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomValidators } from '@shared/custom-validators';
import { ProjectOptionUtils } from '../util/project-option.utils';
import { ProjectOptionDefIds } from '@project/components/project/constants/project-option-def-ids';
import { ProjectOrderingUtils } from '@project/components/project/util/project-ordering-utils';
import { escape } from 'lodash';
import { ArrayUtils } from '@shared/util/array-utils';
import { SaveProjectService } from '../project-save/save-project.service';
import { SaveProjectPopupData } from '../save/save.component';
import { ProjectDataService } from '../../../service/project-data.service';
import { ProjectService } from '../../import-export/service/project.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';
import { ProjectUserModel } from '../../../model/project-user.model';

export interface EditProjectVersionResult {
  isSuccess: boolean;
  newProjectName?: string;
  newProjectId?: number;
}

@Component({
  standalone: false,
  selector: 'app-edit-project-version',
  templateUrl: './edit-project-version.component.html',
})
export class EditProjectVersionComponent implements OnInit {
  @Input() projectSaveInfo: ProjectSaveInfoVO;
  @Input() configName: string = null;
  @Input() selectedProjectGroupProjects: ProjectHeadVO[] = null;
  @Output() public finish = new EventEmitter<EditProjectVersionResult>();

  public form: FormGroup;

  public creator: string;

  private readonly customerMaterialNumberMaxLength: number;

  constructor(
    private bsModalRef: BsModalRef,
    private formBuilder: FormBuilder,
    private userModel: UserModel,
    private alertHelper: AlertHelper,
    private projectModel: ProjectModel,
    private localeResourceManager: LocaleResourceManager,
    private projectDataService: ProjectDataService,
    private saveProjectService: SaveProjectService,
    private projectService: ProjectService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private projectUserModel: ProjectUserModel
  ) {
    this.customerMaterialNumberMaxLength = this.projectUserModel.getProjectOptionDef(ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER).maxLength;
  }

  public ngOnInit(): void {
    if (!this.configName) {
      this.configName = this.projectModel.currentProject.name;
    }
    this.creator = this.getCreatedByInitialValue();
    let customerMaterialNumber = ProjectOptionUtils.getValueFromProjectOptions(
      this.projectModel.currentProject.options,
      ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER
    );
    if (customerMaterialNumber === this.projectService.getDefaultProjectName()) {
      customerMaterialNumber = this.configName;
    }

    this.form = this.formBuilder.group({
      projectVersion: [
        this.getCurrentProjectVersionDisplayString(this.projectSaveInfo, this.projectModel.currentProject),
        Validators.compose([CustomValidators.trimRequired, CustomValidators.maxLength(ProjectOptionUtils.MAX_PROJECT_OPTION_VALUE_LENGTH)]),
      ],
      projectName: [
        this.configName,
        Validators.compose([
          CustomValidators.projectNameValidator(),
          CustomValidators.trimRequired,
          CustomValidators.maxLength(ProjectOptionUtils.MAX_PROJECT_NAME_LENGTH),
        ]),
      ],
      projectCustomerMaterialNumber: this.getTrimmedCustomerMaterialNumber(customerMaterialNumber),
    });
    this.addCustomerMaterialNumberValidator();
    if (!this.selectedProjectGroupProjects) {
      this.form.controls.projectName.disable();
    }
  }

  private addCustomerMaterialNumberValidator(): void {
    if (this.customerMaterialNumberMaxLength) {
      this.form.get('projectCustomerMaterialNumber').setValidators(CustomValidators.maxLength(this.customerMaterialNumberMaxLength));
      this.form.get('projectCustomerMaterialNumber').updateValueAndValidity();
    }
  }

  public close(isSuccess: boolean = false, newProjectName: string = null, newProjectId: number = null): void {
    const result: EditProjectVersionResult = {
      isSuccess,
      newProjectName,
      newProjectId,
    };
    this.bsModalRef.hide();
    this.finish.emit(result);
  }

  public async continue(): Promise<void> {
    if (this.form.invalid) {
      return;
    }
    const projectVersionDisplayString: string = this.form.controls.projectVersion.value;
    if (this.projectSaveInfo && this.projectSaveInfo.existsFinalizedProjectVersionDisplayString(projectVersionDisplayString)) {
      const answer = await this.alertHelper.showAlert('projectRequestPopup.title.project_version_already_used',
        'projectRequestPopup.message.project_version_already_used', {0: escape(projectVersionDisplayString)},
        [AlertButton.YES, AlertButton.NO], "error",
        {
          yesLabel: 'projectRequestPopup.label.edit_project_version',
          noLabel: 'global.label.ignore'
        });
      if (answer === AlertButton.NO) {
        await this.continueSave(projectVersionDisplayString);
      }
    } else {
      await this.continueSave(projectVersionDisplayString);
    }
  }

  public getTrimmedCustomerMaterialNumber(value: string): string {
    return ProjectOptionUtils.trimValueByMaxLength(value, this.customerMaterialNumberMaxLength);
  }

  private async continueSave(projectVersionDisplayString: string): Promise<void> {
    const projectName: string = this.form.controls.projectName.value;
    if (projectName === this.configName) {
      this.finishSave(projectVersionDisplayString);
    } else {
      const existingProject: ProjectHeadVO = this.getAlreadyExistingProjectWithSameName(projectName);
      if (!existingProject) {
        this.finishSave(projectVersionDisplayString, projectName);
      } else {
        const projectSaveInfo: ProjectSaveInfoVO = await this.projectDataService.loadProjectSaveInfo(existingProject.id).toPromise();
        await this.checkSave(projectVersionDisplayString, projectName, projectSaveInfo);
      }
    }
  }

  private async checkSave(projectVersionDisplayString: string, projectName: string, projectSaveInfo: ProjectSaveInfoVO): Promise<void> {
    const existingProject: ProjectHeadVO = projectSaveInfo.projectWithHighestVersion;
    if (existingProject.id === this.projectModel.currentProject.id) {
      if (existingProject.isSharedReadOnly) {
        this.alertHelper.showError('save.title.cannot_override_read_only_project', 'save.message.cannot_override_read_only_project');
        return;
      }
      if (
        ProjectOrderingUtils.isWriteProtected(existingProject, this.userModel.user) &&
        !ProjectOrderingUtils.canCreateNewVersion(existingProject, this.userModel.user)
      ) {
        this.alertHelper.showError(
          'save.title.cannot_override_write_protected_configuration',
          'save.message.cannot_override_write_protected_configuration'
        );
        return;
      }
      const data = await this.projectComponentPresenter.openProjectSaveRequest(projectSaveInfo, false, true);
      await this.changeSaveMethod(projectVersionDisplayString, data);
    } else {
      if (ProjectOrderingUtils.isVersionLocked(existingProject, this.userModel.user)) {
        this.alertHelper.showError(
          'save.title.cannot_override_version_locked_configuration',
          'save.message.cannot_override_version_locked_configuration'
        );
        return;
      }
      const answer = await this.alertHelper.showAlert('save.title.confirm_override_configuration', 'save.message.confirm_override_configuration',
        {0: escape(projectName)}, [AlertButton.YES, AlertButton.NO], "error");

      if (answer === AlertButton.YES) {
        this.finishSave(projectVersionDisplayString, projectName, existingProject.id);
      }
    }
  }

  private finishSave(projectVersionDisplayString: string, newProjectName: string = null, newProjectId: number = null): void {
    this.applyChangesToProject(projectVersionDisplayString);
    this.close(true, newProjectName, newProjectId);
  }

  private applyChangesToProject(projectVersionDisplayString: string): void {
    const createdBy: string = ProjectOptionUtils.getCleanedProjectOptionValue(this.creator);
    ProjectUtils.setOrUpdateProjectOption(
      ProjectOptionDefIds.CREATED_BY,
      createdBy,
      this.projectModel.currentProject,
      this.projectUserModel.projectOptionDefs
    );
    const customMaterialNumber: string =
      ProjectOptionUtils.getCleanedProjectOptionValue(this.form.controls.projectCustomerMaterialNumber.value) ||
      this.getTrimmedCustomerMaterialNumber(this.form.controls.projectName.value);
    ProjectUtils.setOrUpdateProjectOption(
      ProjectOptionDefIds.CUSTOM_MATERIAL_NUMBER,
      customMaterialNumber,
      this.projectModel.currentProject,
      this.projectUserModel.projectOptionDefs
    );
    ProjectUtils.setVersionDisplayString(this.projectModel.currentProject, this.projectUserModel.projectOptionDefs, projectVersionDisplayString);
  }

  private getCreatedByInitialValue(): string {
    let creator: string = ProjectOptionUtils.getValueFromProjectOptions(this.projectModel.currentProject.options, ProjectOptionDefIds.CREATED_BY);
    if (!creator) {
      creator = this.userModel.userProfile.isRegisteredUserAccount()
        ? this.userModel.userProfile.user.getDisplayName()
        : this.localeResourceManager.getValueByKey('confNameComponent.label.guest');
    } else if (
      this.userModel.userProfile &&
      this.userModel.userProfile.isRegisteredUserAccount() &&
      creator === this.localeResourceManager.getValueByKey('confNameComponent.label.guest')
    ) {
      creator = this.userModel.userProfile.user.getDisplayName();
    }
    return creator;
  }

  private getAlreadyExistingProjectWithSameName(projectName: string): ProjectHeadVO {
    if (ArrayUtils.isNotEmpty(this.selectedProjectGroupProjects)) {
      for (const project of this.selectedProjectGroupProjects) {
        if (project.name === projectName) {
          return project;
        }
      }
    }
    return null;
  }

  private async changeSaveMethod(projectVersionDisplayString: string, data: SaveProjectPopupData): Promise<void> {
    if (data.detail > 0) {
      this.applyChangesToProject(projectVersionDisplayString);
      this.close();
      await this.saveProjectService.changeSaveMethod(data, this.form.controls.projectName.value);
    }
  }

  private getCurrentProjectVersionDisplayString(projectSaveInfo: ProjectSaveInfoVO, project: ProjectVO): string {
    if (projectSaveInfo && projectSaveInfo.projectWithHighestVersion && projectSaveInfo.projectId !== project.id) {
      // SMAR-68: In case projectSaveInfo is from different project that should be overwritten,
      // we want to prefill its highest project version, and not the version of current source project.
      return projectSaveInfo.projectWithHighestVersion.currentVersionDisplayString;
    } else {
      return ProjectUtils.getVersionDisplayString(project, false);
    }
  }
}
